import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchWithAuth } from "../components/auth";

export const usePaginatedList = (endpoint, { defaultPageSize = 10 } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPageState] = useState(() =>
    parseInt(searchParams.get("page") || "1", 10)
  );
  const [pageSize, setPageSizeState] = useState(() =>
    parseInt(searchParams.get("pageSize") || String(defaultPageSize), 10)
  );
  const [ordering, setOrderingState] = useState(
    () => searchParams.get("ordering") || ""
  );
  const [filters, setFiltersState] = useState(() => {
    const initial = {};
    searchParams.forEach((value, key) => {
      if (!["page", "pageSize", "ordering"].includes(key)) {
        initial[key] = value;
      }
    });
    return initial;
  });

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchCounter, setFetchCounter] = useState(0);

  // Debounce filters → debouncedFilters (300 ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch data when pagination/filter/sort state changes
  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (ordering) params.set("ordering", ordering);
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    setLoading(true);
    setError(null);

    fetchWithAuth(
      `${process.env.REACT_APP_ICC_TOOL_API_HOST}${endpoint}?${params.toString()}`,
      { signal: controller.signal }
    )
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.json();
      })
      .then((data) => {
        setResults(data.results ?? []);
        setCount(data.count ?? 0);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          setError(err?.message || "Failed to load data");
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [endpoint, page, pageSize, debouncedFilters, ordering, fetchCounter]);

  // Sync state → URL (replace history entry, omit defaults)
  useEffect(() => {
    const params = {};
    if (page !== 1) params.page = String(page);
    if (pageSize !== defaultPageSize) params.pageSize = String(pageSize);
    if (ordering) params.ordering = ordering;
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    setSearchParams(params, { replace: true });
  }, [page, pageSize, filters, ordering, defaultPageSize, setSearchParams]);

  const setPage = useCallback((newPage) => setPageState(newPage), []);

  const setPageSize = useCallback((newSize) => {
    setPageSizeState(newSize);
    setPageState(1);
  }, []);

  const setFilter = useCallback((key, value) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
    setPageState(1);
  }, []);

  const setOrdering = useCallback(
    (newOrdering) => setOrderingState(newOrdering),
    []
  );

  const refetch = useCallback(() => setFetchCounter((c) => c + 1), []);

  return {
    results,
    count,
    loading,
    error,
    page,
    pageSize,
    filters,
    ordering,
    setPage,
    setPageSize,
    setFilter,
    setOrdering,
    refetch,
  };
};
